import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ADMIN_ROUTES, AuthAudience, ROUTES } from '@repo/common';
import { Role } from '@repo/frontend-shared';
import { SDSToastProvider, SDSToastViewport } from '@repo/sds/SDSToast';
import { ResourceNotFound } from '@repo/ui/components/elements/ResourceNotFound';
import { FeatureFlagLoader } from '@repo/ui/components/FeatureFlagLoader/FeatureFlagLoader';
import { RequireRole } from '@repo/ui/components/route-guards';
import { ReactRouterSDSProvider } from '@repo/ui/components/SDS/ReactRouterSDSProvider';
import { SettingsLayout } from '@repo/ui/components/settings-layout';
import { CurrentUserContextProvider } from '@repo/ui/contexts/current-user-context';
import { TimezoneProvider } from '@repo/ui/contexts/timezone-provider';
import { I18nLocaleBootstrap } from '@repo/ui/i18n/i18n-locale-bootstrap';
// Settings Pages
import { AvailabilityListPage } from '@repo/ui/pages/settings-pages/availability';
import { BillingPage } from '@repo/ui/pages/settings-pages/billing';
import { IntegrationsPage } from '@repo/ui/pages/settings-pages/integrations';
import { ManageAccountPage } from '@repo/ui/pages/settings-pages/manage-account';
import { NotificationsPage } from '@repo/ui/pages/settings-pages/notifications';
import { UserPage } from '@repo/ui/pages/settings-pages/profile';
import { SecurityPage } from '@repo/ui/pages/settings-pages/security';

import { AdminLayout } from './components/Layout';

// Partners
const AdminPartners = lazy(() => import('./pages/partners').then(m => ({ default: m.AdminPartners })));
const PartnerDetail = lazy(() => import('./pages/partners/PartnerDetail').then(m => ({ default: m.PartnerDetail })));
// Courses
const AdminCourses = lazy(() => import('./pages/courses').then(m => ({ default: m.AdminCourses })));
const AdminCertificates = lazy(() => import('./pages/certificates').then(m => ({ default: m.AdminCertificates })));
const AdminMbaOperations = lazy(() => import('./pages/mba-operations').then(m => ({ default: m.AdminMbaOperations })));
const AdminPaymentDisputes = lazy(() =>
  import('./pages/payment-disputes').then(m => ({ default: m.AdminPaymentDisputes })),
);
const AdminMeetingRefundJobs = lazy(() =>
  import('./pages/meeting-refund-jobs').then(m => ({ default: m.AdminMeetingRefundJobs })),
);
const AddCourse = lazy(() => import('./pages/courses/AddCourse').then(m => ({ default: m.AddCourse })));
const AdminCoursePage = lazy(() =>
  import('./pages/courses/AdminCoursePage').then(m => ({ default: m.AdminCoursePage })),
);
// AI Instructions
const AIInstructionsPage = lazy(() =>
  import('./pages/ai-instructions-page').then(m => ({ default: m.AIInstructionsPage })),
);
// Dashboard
const AdminDashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.AdminDashboard })));
// Discount Deals
const AdminDiscountDeals = lazy(() => import('./pages/discount-deals').then(m => ({ default: m.AdminDiscountDeals })));
const AdminProtectedDownloads = lazy(() =>
  import('./pages/protected-downloads').then(m => ({ default: m.AdminProtectedDownloads })),
);
const AdminAddProtectedDownload = lazy(() =>
  import('./pages/protected-downloads/AddProtectedDownload').then(m => ({ default: m.AdminAddProtectedDownload })),
);
const AdminEditProtectedDownload = lazy(() =>
  import('./pages/protected-downloads/EditProtectedDownload').then(m => ({ default: m.AdminEditProtectedDownload })),
);
const AdminDiscountDealOrder = lazy(() =>
  import('./pages/discount-deals/manage-order').then(m => ({ default: m.AdminDiscountDealOrder })),
);
const AddDiscountDeal = lazy(() =>
  import('./pages/discount-deals/AddDiscountDeal').then(m => ({ default: m.AddDiscountDeal })),
);
const EditDiscountDeal = lazy(() =>
  import('./pages/discount-deals/EditDiscountDeal').then(m => ({ default: m.EditDiscountDeal })),
);
// Events
const AdminEvents = lazy(() => import('./pages/events').then(m => ({ default: m.AdminEvents })));
const AdminAddEvent = lazy(() => import('./pages/events/AddEvent').then(m => ({ default: m.AdminAddEvent })));
const AdminEditEvent = lazy(() => import('./pages/events/EditEvent').then(m => ({ default: m.AdminEditEvent })));
// Extras
const AdminExtras = lazy(() => import('./pages/extras').then(m => ({ default: m.AdminExtras })));
// Auth Pages
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
// Feature Flags
const AdminFeatureFlags = lazy(() => import('./pages/feature-flags').then(m => ({ default: m.AdminFeatureFlags })));
const AddFeatureFlag = lazy(() =>
  import('./pages/feature-flags/AddFeatureFlag').then(m => ({ default: m.AddFeatureFlag })),
);
const EditFeatureFlag = lazy(() =>
  import('./pages/feature-flags/EditFeatureFlag').then(m => ({ default: m.EditFeatureFlag })),
);
// Mentors
const AdminMentors = lazy(() => import('./pages/mentors').then(m => ({ default: m.AdminMentors })));
const AdminAddMentor = lazy(() => import('./pages/mentors/AddMentor').then(m => ({ default: m.AdminAddMentor })));
const AdminEditMentor = lazy(() => import('./pages/mentors/EditMentor').then(m => ({ default: m.AdminEditMentor })));
// Scenarios
const AdminScenarios = lazy(() => import('./pages/scenarios').then(m => ({ default: m.AdminScenarios })));
const AddScenario = lazy(() => import('./pages/scenarios/AddScenario').then(m => ({ default: m.AddScenario })));
const EditScenario = lazy(() => import('./pages/scenarios/EditScenario').then(m => ({ default: m.EditScenario })));
// Community
const AdminCommunity = lazy(() => import('./pages/community').then(m => ({ default: m.AdminCommunity })));
// Tools & Templates
const AdminAddDocumentTemplate = lazy(() =>
  import('./pages/tools-and-templates/AddDocumentTemplate').then(m => ({ default: m.AdminAddDocumentTemplate })),
);
const AddEmailTemplate = lazy(() =>
  import('./pages/tools-and-templates/AddEmailTemplate').then(m => ({ default: m.AddEmailTemplate })),
);
const AdminEditDocumentTemplate = lazy(() =>
  import('./pages/tools-and-templates/EditDocumentTemplate').then(m => ({ default: m.AdminEditDocumentTemplate })),
);
const AdminEditEmailTemplate = lazy(() =>
  import('./pages/tools-and-templates/EditEmailTemplate').then(m => ({ default: m.AdminEditEmailTemplate })),
);
const AdminTemplates = lazy(() =>
  import('./pages/tools-and-templates/Templates').then(m => ({ default: m.AdminTemplates })),
);
// Videos
const AdminVideos = lazy(() => import('./pages/videos').then(m => ({ default: m.AdminVideos })));
const AdminEditVideo = lazy(() => import('./pages/videos/AdminEditVideo').then(m => ({ default: m.AdminEditVideo })));

const ADMIN_ALLOWED_ROLES = [Role.ADMIN, Role.SUPER_ADMIN] as const;

function AppRoutes() {
  return (
    <Routes>
      {/* Use constants for auth routes */}
      <Route path={ADMIN_ROUTES.ADMIN_LOGIN} element={<LoginPage />} />
      <Route path={ADMIN_ROUTES.ADMIN_FORGOTPASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ADMIN_ROUTES.ADMIN_RESETPASSWORD} element={<ResetPasswordPage />} />
      <Route path={`${ADMIN_ROUTES.ADMIN_RESETPASSWORD}/:token`} element={<ResetPasswordPage />} />

      <Route
        path="/*"
        element={
          <RequireRole
            loginRoute={ADMIN_ROUTES.ADMIN_LOGIN}
            roles={ADMIN_ALLOWED_ROLES}
            audience={AuthAudience.Admin}
            preserveRedirect
          >
            <AdminLayout>
              <Routes>
                {/* Root redirect to admin dashboard */}
                <Route path="/" element={<Navigate to={ADMIN_ROUTES.ADMIN_DASHBOARD} replace />} />

                {/* Dashboard */}
                <Route path={ADMIN_ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />

                {/* Partners */}
                <Route path={ADMIN_ROUTES.ADMIN_PARTNERS} element={<AdminPartners />} />
                <Route path={ADMIN_ROUTES.ADMIN_PARTNER_DETAIL_PATTERN} element={<PartnerDetail />} />

                {/* Courses */}
                <Route path={ADMIN_ROUTES.ADMIN_COURSES} element={<AdminCourses />} />
                <Route path={ADMIN_ROUTES.ADMIN_CERTIFICATES} element={<AdminCertificates />} />
                <Route path={ADMIN_ROUTES.ADMIN_MBA_OPERATIONS} element={<AdminMbaOperations />} />
                <Route path={ADMIN_ROUTES.ADMIN_PAYMENT_DISPUTES} element={<AdminPaymentDisputes />} />
                <Route path={ADMIN_ROUTES.ADMIN_MEETING_REFUND_JOBS} element={<AdminMeetingRefundJobs />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_COURSE} element={<AddCourse />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_COURSE_PATTERN} element={<AdminCoursePage />} />

                {/* Videos */}
                <Route path={ADMIN_ROUTES.ADMIN_VIDEOS} element={<AdminVideos />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_VIDEO_PATTERN} element={<AdminEditVideo />} />

                {/* Mentors */}
                <Route path={ADMIN_ROUTES.ADMIN_MENTORS} element={<AdminMentors />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_MENTOR} element={<AdminAddMentor />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_MENTOR_PATTERN} element={<AdminEditMentor />} />

                {/* Events */}
                <Route path={ADMIN_ROUTES.ADMIN_EVENTS} element={<AdminEvents />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_EVENT} element={<AdminAddEvent />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_EVENT_PATTERN} element={<AdminEditEvent />} />

                {/* Tools & Templates */}
                <Route path={ADMIN_ROUTES.ADMIN_TOOLS_AND_TEMPLATES} element={<AdminTemplates />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_DOCUMENT_TEMPLATE} element={<AdminAddDocumentTemplate />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_EMAIL_TEMPLATE} element={<AddEmailTemplate />} />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TOOLS_AND_TEMPLATES_DOCUMENT_TEMPLATE_PATTERN}
                  element={<AdminEditDocumentTemplate />}
                />
                <Route
                  path={ADMIN_ROUTES.ADMIN_TOOLS_AND_TEMPLATES_EMAIL_TEMPLATE_PATTERN}
                  element={<AdminEditEmailTemplate />}
                />

                {/* Scenarios */}
                <Route path={ADMIN_ROUTES.ADMIN_SCENARIOS} element={<AdminScenarios />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_SCENARIO} element={<AddScenario />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_SCENARIO_PATTERN} element={<EditScenario />} />

                <Route path={ADMIN_ROUTES.ADMIN_COMMUNITY} element={<AdminCommunity />} />

                {/* Extras */}
                <Route path={ADMIN_ROUTES.ADMIN_EXTRAS} element={<AdminExtras />} />

                {/* Discount Deals */}
                <Route path={ADMIN_ROUTES.ADMIN_PROTECTED_DOWNLOADS} element={<AdminProtectedDownloads />} />
                <Route path={ADMIN_ROUTES.ADMIN_PROTECTED_DOWNLOAD_NEW} element={<AdminAddProtectedDownload />} />
                <Route
                  path={ADMIN_ROUTES.ADMIN_PROTECTED_DOWNLOAD_EDIT_PATTERN}
                  element={<AdminEditProtectedDownload />}
                />
                <Route path={ADMIN_ROUTES.ADMIN_DISCOUNT_DEALS} element={<AdminDiscountDeals />} />
                <Route path={ADMIN_ROUTES.ADMIN_DISCOUNT_DEALS_ORDER} element={<AdminDiscountDealOrder />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_DISCOUNT_DEAL} element={<AddDiscountDeal />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_DISCOUNT_DEAL_PATTERN} element={<EditDiscountDeal />} />

                {/* Feature Flags */}
                <Route path={ADMIN_ROUTES.ADMIN_FEATURE_FLAGS} element={<AdminFeatureFlags />} />
                <Route path={ADMIN_ROUTES.ADMIN_ADD_FEATURE_FLAGS} element={<AddFeatureFlag />} />
                <Route path={ADMIN_ROUTES.ADMIN_EDIT_FEATURE_FLAGS_PATTERN} element={<EditFeatureFlag />} />

                {/* Custom Instructions */}
                <Route path={ADMIN_ROUTES.ADMIN_AI_INSTRUCTIONS} element={<AIInstructionsPage />} />

                {/* Settings Pages */}
                <Route element={<SettingsLayout />}>
                  <Route path={ROUTES.PROFILE} element={<UserPage />} />
                  <Route path={ROUTES.AVAILABILITY} element={<AvailabilityListPage />} />
                  <Route path={ROUTES.SECURITY} element={<SecurityPage />} />
                  <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
                  <Route path={ROUTES.MANAGE_ACCOUNT} element={<ManageAccountPage />} />
                  <Route path={ROUTES.BILLING} element={<BillingPage />} />
                  <Route path={ROUTES.INTEGRATIONS} element={<IntegrationsPage />} />
                </Route>

                <Route
                  path="*"
                  element={
                    <ResourceNotFound
                      resourceType="Page"
                      backRoute={ADMIN_ROUTES.ADMIN_DASHBOARD}
                      backButtonText="Back to Dashboard"
                    />
                  }
                />
              </Routes>
            </AdminLayout>
          </RequireRole>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <ReactRouterSDSProvider>
      <SDSToastProvider>
        <TimezoneProvider>
          <CurrentUserContextProvider apiUri={__API_URI__}>
            <FeatureFlagLoader>
              <I18nLocaleBootstrap>
                <SDSToastViewport />
                <AppRoutes />
              </I18nLocaleBootstrap>
            </FeatureFlagLoader>
          </CurrentUserContextProvider>
        </TimezoneProvider>
      </SDSToastProvider>
    </ReactRouterSDSProvider>
  );
}
